import React, { useState, forwardRef, useImperativeHandle, useEffect, useCallback } from 'react';
import { fetchVideos } from '../../../utils/TrainingHelpers';
import { phraseSets, baseVariations } from '../../../utils/PhraseSets';

const baseVariationKeys = baseVariations.map(v => v.key);

const PhraseBatchItem = forwardRef(({ 
  phraseId,
  variations,
  setError,
  viewAllUsers,
  onVideoSelect,
  selectedVideos,
  onVideoClick,
  onVideoDelete,
  onEditRating = () => {},
  isLocked,
  recordingUser,
  currentUserId,
  page = 'phrase'
}, ref) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [videos, setVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [allSelected, setAllSelected] = useState(false);
  const [videoCount, setVideoCount] = useState(
    variations.reduce((sum, v) => sum + (v.videoCount || 0), 0)
  );

  const phraseInfo = phraseSets.find(p => p.id === phraseId);
  const phraseText = phraseInfo ? phraseInfo.text : '';
  const expectedVariations = phraseInfo ? phraseInfo.variations.length : 0;

  useEffect(() => {
    setVideoCount(variations.reduce((sum, v) => sum + (v.videoCount || 0), 0));
  }, [variations]);

  useEffect(() => {
    if (videos.length > 0) {
      const areAllSelected = videos.every(video => selectedVideos.has(video.id));
      setAllSelected(areAllSelected);
    }
  }, [selectedVideos, videos]);

  const handleSelectAll = (e) => {
    e.stopPropagation();
    const newAllSelected = !allSelected;

    const videoUpdates = videos.map(video => {
      if (newAllSelected && !selectedVideos.has(video.id)) {
        return video.id;
      } else if (!newAllSelected && selectedVideos.has(video.id)) {
        return video.id;
      }
      return null;
    }).filter(id => id !== null);

    videoUpdates.forEach(videoId => {
      onVideoSelect(videoId);
    });
  };

  const refreshVideos = useCallback(async () => {
    if (!isExpanded) return;
    setIsLoading(true);
    try {
      const filters = [phraseId];
      const serverVideos = [];
      await fetchVideos(
        (vs) => serverVideos.push(...vs),
        setError,
        viewAllUsers,
        0,
        100,
        false,
        filters,
        null,
        recordingUser,
        page
      );
      setVideos(serverVideos);
      setVideoCount(serverVideos.length);
    } catch (error) {
      console.error(`Failed to refresh videos for phrase ${phraseId}`, error);
    } finally {
      setIsLoading(false);
    }
  }, [isExpanded, phraseId, recordingUser, viewAllUsers, page, setError]);

  useEffect(() => {
    if (isExpanded) {
      refreshVideos();
    }
  }, [isExpanded, refreshVideos]);

  const handleExpand = () => {
    setIsExpanded(prev => !prev);
  };

  const getVariationLabel = (video) => {
    if (!video.gesture) return '';
    const parts = video.gesture.split('_');
    return parts[1] || '';
  };

  const handleVideoClick = (video, e) => {
    e.stopPropagation();
    onVideoClick(video, e); // VideoPlayback will fetch using video.id
  };

  const handleVideoDelete = (videoId, batchId) => {
    onVideoDelete(videoId, batchId);
  };

  useImperativeHandle(ref, () => ({
    refreshVideos,
    decrementCount: () => setVideoCount(count => count - 1),
    decrementCountByAmount: (amount) => setVideoCount(count => count - amount),
    removeVideo: (id) => setVideos(vs => vs.filter(v => v.id !== id)),
    removeVideos: (ids) => setVideos(vs => vs.filter(v => !ids.includes(v.id))),
    hasVideo: (id) => videos.some(v => v.id === id),
  }));

  return (
    <div className={`batch-item ${isExpanded ? 'expanded' : ''}`}>
      <div className="batch-header" onClick={handleExpand}>
        <div className="batch-info">
          <div className="batch-main-info">
            <span className="batch-gesture">{phraseText}</span>
            {!isExpanded && videoCount < expectedVariations && expectedVariations > 0 && (
              <img
                src="/icons/caution.svg"
                alt="Incomplete"
                title="Phrase incomplete"
                className="batch-incomplete-icon"
              />
            )}
            {!isExpanded && videoCount >= expectedVariations && expectedVariations > 0 && (
              <img
                src="/icons/checkmark.svg"
                alt="Complete"
                title="Phrase complete"
                className="batch-complete-icon"
              />
            )}
            {isExpanded && videos.length > 0 && (
              <button
                onClick={handleSelectAll}
                className="select-all-button"
                style={{ marginLeft: '10px', padding: '4px 8px', fontSize: '0.9em', cursor: 'pointer' }}
              >
                {allSelected ? 'Deselect All' : 'Select All'}
              </button>
            )}
          </div>
          {viewAllUsers && recordingUser && (
            <span className="batch-username">User: {recordingUser}</span>
          )}
          <span className="phrase-text">{`Variations Recorded: (${videoCount}/${expectedVariations})`}</span>
        </div>
        <div className="batch-expand-icon">{isExpanded ? '▼' : '▶'}</div>
      </div>
      {isExpanded && (
        <div className="batch-videos">
          {isLoading ? (
            <div className="loading-container">
              <span>Loading videos...</span>
              <div className="loading-spinner">
                <div className="spinner"></div>
              </div>
            </div>
          ) : videos.length > 0 ? (
            <ul className="videos-list">
              {videos.map((video) => (
                <li key={video.id} onClick={(e) => handleVideoClick(video, e)}>
                  <input
                    type="checkbox"
                    checked={selectedVideos.has(video.id)}
                    onChange={() => onVideoSelect(video.id)}
                    className="video-checkbox"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="video-thumbnail">
                    <img
                      src={video.thumbnailUrl || "/video-thumbnail.png"}
                      alt={`${getVariationLabel(video)} thumbnail`}
                      className="thumbnail-image"
                      onError={(e) => {
                        e.target.src = "/video-thumbnail.png";
                      }}
                    />
                    <div className="gesture-overlay">{getVariationLabel(video)}</div>
                  </div>
                  <div className="video-info">
                    <p>{getVariationLabel(video)}</p>
                    <p>{new Date(video.timestamp).toLocaleTimeString([], { hour12: true })}</p>
                    {viewAllUsers && video.username && (
                      <p className="video-username">User: {video.username}</p>
                    )}
                    {video.rating?.sentiment !== undefined && video.rating.sentiment !== null && (
                      <p className="video-sentiment">Sentiment: {video.rating.sentiment}</p>
                    )}
                  </div>
                  <div className="video-actions">
                    {!baseVariationKeys.includes(getVariationLabel(video)) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditRating(video);
                        }}
                        className="edit-rating-button"
                        title="Edit Rating"
                      >
                        <img src="/icons/pencil.svg" alt="Edit Rating" className="icon" />
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleVideoDelete(video.id, video.batchId);
                      }}
                      className="delete-button"
                      title="Delete video"
                    >
                      <img src="/icons/red-trash-can-icon.svg" alt="Delete" className="icon" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="no-videos-message">No videos found for this phrase.</div>
          )}
        </div>
      )}
      {isLocked && isExpanded && (
        <div className="batch-lock-overlay">
          <div className="batch-lock-message">
            <img src="/icons/lock.svg" alt="Locked" className="icon" />
            Replacement batch pending...
          </div>
        </div>
      )}
    </div>
  );
});

export default PhraseBatchItem;
